/**
 * Timeline scrubber for the public viewer — a thin CSS/flex bar (NOT
 * bespoke SVG) over the session's UTC span:
 *
 *   ├─────────▓▓▓▓▓▓▓▓▓───────────┤
 *             ▲ playhead        ▲ LIVE edge
 *   ▓ = video-available coverage (from trailCoverage)
 *
 * Click / drag anywhere → onSeek(utcMs). Shares the same playheadUtc as
 * the map marker + chart cursor, so all three move together. Designed to
 * sit edge-to-edge under the speed chart in both desktop + mobile panels.
 */

import React, { useRef } from 'react';

interface ScrubberProps {
  /** UTC span of the timeline (match the chart domain: trail first/last sample). */
  startUtc: number;
  endUtc: number;
  /** Current video playhead UTC; null while pre-PDT/buffering. */
  playheadUtc: number | null;
  /** Video-available intervals (UTC ms) — drawn as the filled coverage. */
  coverage?: Array<[number, number]> | null;
  onSeek?: (utcMs: number) => void;
}

export const Scrubber: React.FC<ScrubberProps> = ({
  startUtc,
  endUtc,
  playheadUtc,
  coverage,
  onSeek,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const span = Math.max(1, endUtc - startUtc);
  const pct = (utc: number) => Math.max(0, Math.min(100, ((utc - startUtc) / span) * 100));

  const seekAt = (clientX: number) => {
    if (!onSeek || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    if (r.width <= 0) return;
    const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    onSeek(startUtc + f * span);
  };

  return (
    <div
      ref={ref}
      className="relative h-2 w-full cursor-pointer touch-none rounded-full bg-bg-base"
      role="slider"
      aria-label="Seek timeline"
      aria-valuemin={startUtc}
      aria-valuemax={endUtc}
      aria-valuenow={playheadUtc ?? startUtc}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        seekAt(e.clientX);
      }}
      onPointerMove={(e) => {
        if (e.buttons) seekAt(e.clientX);
      }}
    >
      {/* Coverage fill — where playable video exists. */}
      {(coverage ?? []).map(([s, e], i) => {
        const left = pct(s);
        const width = Math.max(0.6, pct(e) - left);
        return (
          <div
            key={`${s}-${e}-${i}`}
            className="absolute inset-y-0 rounded-full bg-status-buffered/45"
            style={{ left: `${left}%`, width: `${width}%` }}
          />
        );
      })}
      {/* LIVE / telemetry edge — right boundary. */}
      <div className="absolute inset-y-0 right-0 w-px bg-fg/40" aria-hidden />
      {/* Playhead handle. */}
      {playheadUtc != null && (
        <div
          className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-bg-base bg-accent-cyan shadow"
          style={{ left: `${pct(playheadUtc)}%` }}
        />
      )}
    </div>
  );
};

export default Scrubber;
